/**
 * /cost-dashboard — Custo de IA (OpenAI) e Meta (WhatsApp).
 *
 * Duas partes:
 *   1. Visão de custo agregado (`GET /admin/metrics/cost`), com seletor de
 *      período (7/14/30d) e breakdown por modelo de IA / categoria Meta.
 *      Custo Meta mostra "real" (captura de billing) quando disponível,
 *      senão cai pra "estimado" (contagem de templates enviados) — os dois
 *      nunca são somados automaticamente.
 *   2. Cadastro manual de preço (`/admin/pricing/*`) — leitura para
 *      qualquer papel, criação/remoção somente admin. O sistema nunca
 *      inventa preço (Constitution Art. IV): sem cadastro, o custo daquele
 *      modelo/categoria aparece como "sem preço" e não entra no total.
 *   3. Custo de Infra (`/admin/pricing/infra`, Story 7.31) — cadastro manual
 *      do custo mensal da VM de produção, exibido ao lado do custo de IA.
 *      Não entra no "Custo total" agregado (esse é só IA + Meta, Story
 *      7.29) — é um valor fixo mensal, mostrado separadamente.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import {
  AiModelPricingForm,
  type AiModelPricingFormValue,
} from '@/components/cost/AiModelPricingForm';
import {
  MetaConversationPricingForm,
  type MetaConversationPricingCategory,
} from '@/components/cost/MetaConversationPricingForm';
import { InfraPricingForm } from '@/components/cost/InfraPricingForm';

type Period = 7 | 14 | 30;

interface CostBreakdownRow {
  costUsd: number | null;
}

interface AiCostModelBreakdown extends CostBreakdownRow {
  provider: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  audioSeconds: number;
}

interface MetaCostCategoryBreakdown extends CostBreakdownRow {
  category: string;
  conversationCount: number;
}

interface MetaCostBucket {
  totalCostUsd: number;
  byCategory: MetaCostCategoryBreakdown[];
  unpricedCategories: string[];
}

interface CostDashboard {
  periodDays: number;
  generatedAt: string;
  ai: {
    periodFrom: string;
    periodTo: string;
    totalCostUsd: number;
    byModel: AiCostModelBreakdown[];
    unpricedModels: string[];
  };
  meta: {
    periodFrom: string;
    periodTo: string;
    real: MetaCostBucket & { coversFullPeriod: boolean };
    estimated: MetaCostBucket & { note: string };
  };
  totalCostUsd: number;
  metaUsesReal: boolean;
}

interface AiModelPricingDto {
  id: string;
  provider: string;
  model: string;
  unit: 'token_1k' | 'audio_second';
  pricePromptPerUnit: number | null;
  priceCompletionPerUnit: number | null;
  pricePerUnit: number | null;
  currency: string;
  effectiveFrom: string;
}

interface MetaConversationPricingDto {
  id: string;
  category: string;
  pricePerConversation: number;
  currency: string;
  effectiveFrom: string;
}

interface InfraPricingDto {
  id: string;
  description: string;
  monthlyValue: number;
  currency: string;
  effectiveFrom: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** USD com até 4 casas — custos de IA costumam ser frações de centavo. */
function formatUsd(value: number | null): string {
  if (value === null) return 'sem preço';
  return `US$ ${value.toFixed(value < 1 ? 4 : 2)}`;
}

/** Preço unitário (ex: por 1k tokens) — no máximo 4 casas, sem zeros à direita. */
function formatUnitPrice(value: number | null): string {
  if (value === null) return '—';
  return value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

export function CostDashboardPage() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';

  const [period, setPeriod] = useState<Period>(30);
  const [createAiOpen, setCreateAiOpen] = useState(false);
  const [createMetaOpen, setCreateMetaOpen] = useState(false);
  const [createInfraOpen, setCreateInfraOpen] = useState(false);

  const costQuery = useQuery({
    queryKey: ['cost-dashboard', period],
    queryFn: () => apiClient.get<CostDashboard>(`/admin/metrics/cost?period=${period}`),
    refetchInterval: 60_000,
  });

  const aiPricingQuery = useQuery({
    queryKey: ['pricing', 'ai-models'],
    queryFn: () => apiClient.get<AiModelPricingDto[]>('/admin/pricing/ai-models'),
  });

  const metaPricingQuery = useQuery({
    queryKey: ['pricing', 'meta-categories'],
    queryFn: () => apiClient.get<MetaConversationPricingDto[]>('/admin/pricing/meta-categories'),
  });

  const infraPricingQuery = useQuery({
    queryKey: ['pricing', 'infra'],
    queryFn: () => apiClient.get<InfraPricingDto[]>('/admin/pricing/infra'),
  });

  const createAiPricingMutation = useMutation({
    mutationFn: (body: AiModelPricingFormValue) =>
      apiClient.post<AiModelPricingDto>('/admin/pricing/ai-models', body),
    onSuccess: () => {
      toast.success('Preço de modelo de IA cadastrado.');
      setCreateAiOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'ai-models'] });
      void queryClient.invalidateQueries({ queryKey: ['cost-dashboard'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao cadastrar preço.')),
  });

  const deleteAiPricingMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/pricing/ai-models/${id}`),
    onSuccess: () => {
      toast.success('Preço removido.');
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'ai-models'] });
      void queryClient.invalidateQueries({ queryKey: ['cost-dashboard'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao remover preço.')),
  });

  const createMetaPricingMutation = useMutation({
    mutationFn: (body: {
      category: MetaConversationPricingCategory;
      pricePerConversation: number;
    }) => apiClient.post<MetaConversationPricingDto>('/admin/pricing/meta-categories', body),
    onSuccess: () => {
      toast.success('Preço de categoria Meta cadastrado.');
      setCreateMetaOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'meta-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['cost-dashboard'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao cadastrar preço.')),
  });

  const deleteMetaPricingMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/pricing/meta-categories/${id}`),
    onSuccess: () => {
      toast.success('Preço removido.');
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'meta-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['cost-dashboard'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao remover preço.')),
  });

  const createInfraPricingMutation = useMutation({
    mutationFn: (body: { description: string; monthlyValue: number }) =>
      apiClient.post<InfraPricingDto>('/admin/pricing/infra', body),
    onSuccess: () => {
      toast.success('Custo de infra cadastrado.');
      setCreateInfraOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'infra'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao cadastrar custo de infra.')),
  });

  const deleteInfraPricingMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/pricing/infra/${id}`),
    onSuccess: () => {
      toast.success('Custo de infra removido.');
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'infra'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao remover custo de infra.')),
  });

  const data = costQuery.data;
  const metaUsesReal = data?.metaUsesReal ?? false;
  const metaShown = data ? (metaUsesReal ? data.meta.real : data.meta.estimated) : null;
  // `infraPricingQuery.data` vem ordenado desc por effectiveFrom (histórico
  // completo, inclusive linhas agendadas no futuro) — o "vigente agora" é a
  // primeira cujo effectiveFrom já passou, não simplesmente a mais recente.
  const currentInfraPricing =
    infraPricingQuery.data?.find((row) => new Date(row.effectiveFrom).getTime() <= Date.now()) ??
    null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Custo de IA</h1>
          <p className="text-sm text-muted-foreground">
            Gasto com OpenAI (chat + transcrição) e Meta (WhatsApp Cloud API).
          </p>
        </div>
        <div
          role="group"
          aria-label="Período"
          className="inline-flex shrink-0 rounded-md border bg-background p-0.5 text-sm"
        >
          {([7, 14, 30] as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              aria-pressed={period === p}
              className={`rounded px-3 py-1 transition-colors ${
                period === p
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      {costQuery.error ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            {humanizeError(costQuery.error, 'Falha ao carregar custo.')}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Custo total ({period}d)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div data-testid="kpi-total-cost" className="text-2xl font-semibold">
              {costQuery.isLoading ? '—' : formatUsd(data?.totalCostUsd ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Custo IA (OpenAI)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div data-testid="kpi-ai-cost" className="text-2xl font-semibold">
              {costQuery.isLoading ? '—' : formatUsd(data?.ai.totalCostUsd ?? 0)}
            </div>
            {data && data.ai.unpricedModels.length > 0 ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Sem preço: {data.ai.unpricedModels.join(', ')}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Custo Meta (WhatsApp)
            </CardTitle>
            {data ? (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  metaUsesReal
                    ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                    : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                }`}
              >
                {metaUsesReal ? 'real' : 'estimado'}
              </span>
            ) : null}
          </CardHeader>
          <CardContent className="pt-0">
            <div data-testid="kpi-meta-cost" className="text-2xl font-semibold">
              {costQuery.isLoading ? '—' : formatUsd(metaShown?.totalCostUsd ?? 0)}
            </div>
            {!metaUsesReal && data ? (
              <p className="mt-1 text-xs text-muted-foreground">{data.meta.estimated.note}</p>
            ) : null}
            {!metaUsesReal && data && data.meta.real.totalCostUsd > 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Captura real parcial no período: {formatUsd(data.meta.real.totalCostUsd)} (não cobre
                o período inteiro ainda, por isso não é o número principal).
              </p>
            ) : null}
            {metaShown && metaShown.unpricedCategories.length > 0 ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Sem preço: {metaShown.unpricedCategories.join(', ')}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Custo Infra (mensal)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div data-testid="kpi-infra-cost" className="text-2xl font-semibold">
              {infraPricingQuery.isLoading
                ? '—'
                : currentInfraPricing
                  ? formatUsd(currentInfraPricing.monthlyValue)
                  : 'sem preço'}
            </div>
            {currentInfraPricing ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {currentInfraPricing.description}
              </p>
            ) : (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Nenhum custo de infra cadastrado.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por modelo (IA)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {costQuery.isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Modelo</th>
                      <th className="px-4 py-2">Prompt tk</th>
                      <th className="px-4 py-2">Completion tk</th>
                      <th className="px-4 py-2">Áudio (s)</th>
                      <th className="px-4 py-2 text-right">Custo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.ai.byModel.length > 0 ? (
                      data.ai.byModel.map((row) => (
                        <tr key={row.model} className="border-b last:border-b-0">
                          <td className="px-4 py-2 font-mono text-xs">{row.model}</td>
                          <td className="px-4 py-2">{row.promptTokens}</td>
                          <td className="px-4 py-2">{row.completionTokens}</td>
                          <td className="px-4 py-2">{row.audioSeconds}</td>
                          <td className="px-4 py-2 text-right">{formatUsd(row.costUsd)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          Sem uso no período.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Por categoria (Meta) — {metaUsesReal ? 'real' : 'estimado'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {costQuery.isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Categoria</th>
                      <th className="px-4 py-2">Conversas</th>
                      <th className="px-4 py-2 text-right">Custo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metaShown && metaShown.byCategory.length > 0 ? (
                      metaShown.byCategory.map((row) => (
                        <tr key={row.category} className="border-b last:border-b-0">
                          <td className="px-4 py-2">{row.category}</td>
                          <td className="px-4 py-2">{row.conversationCount}</td>
                          <td className="px-4 py-2 text-right">{formatUsd(row.costUsd)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                          Sem dados no período.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Preços de modelos de IA</CardTitle>
            <Button
              size="sm"
              onClick={() => setCreateAiOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Somente administradores.'}
            >
              <Plus className="mr-1" aria-hidden />
              Novo preço
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {aiPricingQuery.isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Modelo</th>
                      <th className="px-4 py-2">Unidade</th>
                      <th className="px-4 py-2">Preço</th>
                      <th className="px-4 py-2">Vigente desde</th>
                      <th className="px-4 py-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiPricingQuery.data && aiPricingQuery.data.length > 0 ? (
                      aiPricingQuery.data.map((row) => (
                        <tr key={row.id} className="border-b last:border-b-0">
                          <td className="px-4 py-2 font-mono text-xs">
                            {row.provider}/{row.model}
                          </td>
                          <td className="px-4 py-2">{row.unit}</td>
                          <td className="px-4 py-2 text-xs">
                            {row.unit === 'token_1k'
                              ? `P ${formatUnitPrice(row.pricePromptPerUnit)} / C ${formatUnitPrice(row.priceCompletionPerUnit)}`
                              : formatUnitPrice(row.pricePerUnit)}{' '}
                            {row.currency}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {formatDateTime(row.effectiveFrom)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!isAdmin || deleteAiPricingMutation.isPending}
                              title={isAdmin ? undefined : 'Somente administradores.'}
                              aria-label={`Remover preço ${row.provider}/${row.model}`}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Remover o preço de ${row.provider}/${row.model}? O dashboard de custo usa sempre o preço vigente para o período inteiro consultado — sem preço cadastrado, o custo desse modelo passa a aparecer como "sem preço" imediatamente, inclusive para uso passado já exibido.`,
                                  )
                                ) {
                                  deleteAiPricingMutation.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          Nenhum preço cadastrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Preços de categoria Meta</CardTitle>
            <Button
              size="sm"
              onClick={() => setCreateMetaOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Somente administradores.'}
            >
              <Plus className="mr-1" aria-hidden />
              Novo preço
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {metaPricingQuery.isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">Categoria</th>
                      <th className="px-4 py-2">Preço / conversa</th>
                      <th className="px-4 py-2">Vigente desde</th>
                      <th className="px-4 py-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metaPricingQuery.data && metaPricingQuery.data.length > 0 ? (
                      metaPricingQuery.data.map((row) => (
                        <tr key={row.id} className="border-b last:border-b-0">
                          <td className="px-4 py-2">{row.category}</td>
                          <td className="px-4 py-2 text-xs">
                            {formatUnitPrice(row.pricePerConversation)} {row.currency}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {formatDateTime(row.effectiveFrom)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!isAdmin || deleteMetaPricingMutation.isPending}
                              title={isAdmin ? undefined : 'Somente administradores.'}
                              aria-label={`Remover preço ${row.category}`}
                              onClick={() => {
                                if (
                                  window.confirm(`Remover o preço da categoria ${row.category}?`)
                                ) {
                                  deleteMetaPricingMutation.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                          Nenhum preço cadastrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Custo de Infra</CardTitle>
          <Button
            size="sm"
            onClick={() => setCreateInfraOpen(true)}
            disabled={!isAdmin}
            title={isAdmin ? undefined : 'Somente administradores.'}
          >
            <Plus className="mr-1" aria-hidden />
            Novo custo
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {infraPricingQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Descrição</th>
                    <th className="px-4 py-2">Valor mensal</th>
                    <th className="px-4 py-2">Vigente desde</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {infraPricingQuery.data && infraPricingQuery.data.length > 0 ? (
                    infraPricingQuery.data.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="px-4 py-2">{row.description}</td>
                        <td className="px-4 py-2 text-xs">
                          {formatUnitPrice(row.monthlyValue)} {row.currency}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {formatDateTime(row.effectiveFrom)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!isAdmin || deleteInfraPricingMutation.isPending}
                            title={isAdmin ? undefined : 'Somente administradores.'}
                            aria-label={`Remover custo de infra ${row.description}`}
                            onClick={() => {
                              if (
                                window.confirm(`Remover o custo de infra "${row.description}"?`)
                              ) {
                                deleteInfraPricingMutation.mutate(row.id);
                              }
                            }}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum custo de infra cadastrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data ? (
        <div className="flex items-center justify-end text-xs text-muted-foreground">
          Atualizado em {formatDateTime(data.generatedAt)}
        </div>
      ) : null}

      <Dialog open={createAiOpen} onOpenChange={setCreateAiOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo preço de modelo de IA</DialogTitle>
            <DialogDescription>
              Cadastro manual — o sistema nunca infere ou busca preço automaticamente.
            </DialogDescription>
          </DialogHeader>
          <AiModelPricingForm
            submitting={createAiPricingMutation.isPending}
            onCancel={() => setCreateAiOpen(false)}
            onSubmit={(next) => createAiPricingMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={createMetaOpen} onOpenChange={setCreateMetaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo preço de categoria Meta</DialogTitle>
            <DialogDescription>
              Cadastro manual — o sistema nunca infere ou busca preço automaticamente.
            </DialogDescription>
          </DialogHeader>
          <MetaConversationPricingForm
            submitting={createMetaPricingMutation.isPending}
            onCancel={() => setCreateMetaOpen(false)}
            onSubmit={(next) => createMetaPricingMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={createInfraOpen} onOpenChange={setCreateInfraOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo custo de infra</DialogTitle>
            <DialogDescription>
              Cadastro manual — o sistema nunca infere ou busca custo de infra automaticamente.
            </DialogDescription>
          </DialogHeader>
          <InfraPricingForm
            submitting={createInfraPricingMutation.isPending}
            onCancel={() => setCreateInfraOpen(false)}
            onSubmit={(next) => createInfraPricingMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CostDashboardPage;
